import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import { AssignmentStudentsWithSkillStatesQuery } from '../../graphQLOperations/__generated__/AssignmentStudentsWithSkillStatesQuery.graphql';
import { getStudentsWithSkillStates } from '../../graphQLOperations/Assignment';
import { Checkbox } from 'primereact/checkbox';
import { Student, StudentsLists, studentRowKey } from './AssignmentSidebarContainer';
import { Button } from 'primereact/button';
import { TabPanel, TabView } from 'primereact/tabview';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../analytics/dataMasking';
import { Class } from '../../models/Class';
import { Tooltip } from 'primereact/tooltip';
import { sortClassesList } from '../../utils/sortClassesList';

interface Props {
  queryReference: PreloadedQuery<AssignmentStudentsWithSkillStatesQuery, Record<string, unknown>>;
  studentsLists: StudentsLists;
  selectedClasses: Class[];
  notIncludeNoPredPassed: boolean;
  setNotIncludeNoPredPassed: Dispatch<SetStateAction<boolean>>;
  /** Records for analytics that the "Do Not Include" shortcut was used. Never unset. */
  markDoNotIncludeUsed: () => void;
  setStudentsLists: Dispatch<SetStateAction<StudentsLists>>;
}

const multipleClassesMessage =
  'This student will receive an assignment for each class in which they are selected. Completing one assignment will automatically complete any other assignments for the same skill or pathway.';

export const AssignmentSidebarStudentsList: FC<Props> = ({
  queryReference,
  notIncludeNoPredPassed,
  studentsLists,
  selectedClasses,
  setNotIncludeNoPredPassed,
  markDoNotIncludeUsed,
  setStudentsLists,
}) => {
  const { studentSkillAndEssentialPredecessorsProgress } = usePreloadedQuery(
    getStudentsWithSkillStates,
    queryReference,
  );
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const selectedClassIds = new Set(selectedClasses.map(c => c.id));
    const alreadyMastered: Student[] = [];
    const readyToLearn: Student[] = [];
    const buildingFoundations: Student[] = [];

    studentSkillAndEssentialPredecessorsProgress.forEach(student => {
      const rowClassIds = (student.student?.classes ?? [])
        .map(c => c?.id)
        .filter((id): id is string => !!id && selectedClassIds.has(id));

      const skills = student.skillStatuses.map(skill => ({
        id: skill.skill.id,
        name: `${skill.skill.name} (${skill.skill.code})`,
        passed: skill.isPassedOrInferred,
        readinessScore: skill.readinessScore,
      }));

      rowClassIds.forEach(classId => {
        const studentPayload: Student = {
          studentFirstName: student.student?.firstName ?? '',
          studentLastName: student.student?.lastName ?? '',
          studentId: student.student?.id || '',
          publicUserId: student.student?.publicUserId || '',
          classId,
          selected: true,
          selectedClassCount: rowClassIds.length,
          skills,
        };

        if (student.skillStatuses[0].isPassedOrInferred) {
          alreadyMastered.push(studentPayload);
        } else if (student.skillStatuses.slice(1).every(skill => skill.isPassedOrInferred)) {
          readyToLearn.push(studentPayload);
        } else {
          buildingFoundations.push({
            ...studentPayload,
            noPredPassed: student.skillStatuses.every(sk => !sk.isPassedOrInferred),
          });
        }
      });
    });
    setStudentsLists(prev => ({
      ...prev,
      alreadyMastered: { ...prev.alreadyMastered, list: alreadyMastered },
      readyToLearn: { ...prev.readyToLearn, list: readyToLearn },
      buildingFoundations: { ...prev.buildingFoundations, list: buildingFoundations },
    }));
  }, [studentSkillAndEssentialPredecessorsProgress, selectedClasses]);

  const orderedClasses = sortClassesList(selectedClasses);

  const studentsFor = (listName: keyof StudentsLists, classId: string) =>
    studentsLists[listName].list
      .filter(student => student.classId === classId)
      .sort((a, b) => a.studentLastName.localeCompare(b.studentLastName, undefined, { sensitivity: 'base' }));

  const updateList = (listName: keyof StudentsLists, updater: (student: Student) => Student) => {
    const newList = studentsLists[listName].list.map(updater);
    if (newList.some(item => item.noPredPassed && item.selected)) {
      setNotIncludeNoPredPassed(false);
    }
    setStudentsLists(prev => ({ ...prev, [listName]: { ...prev[listName], list: newList } }));
  };

  const handleCheck = (student: Student, listName: keyof StudentsLists) => {
    updateList(listName, item =>
      studentRowKey(item) === studentRowKey(student) ? { ...item, selected: !item.selected } : item,
    );
  };

  const selectAllInClass = (listName: keyof StudentsLists, classId: string, flag: boolean) => {
    updateList(listName, item => (item.classId === classId ? { ...item, selected: flag } : item));
  };

  const dontIncludeNoPredPassed = () => {
    if (notIncludeNoPredPassed) {
      setNotIncludeNoPredPassed(false);
    } else {
      setStudentsLists(prev => ({
        ...prev,
        buildingFoundations: {
          ...prev.buildingFoundations,
          list: prev.buildingFoundations.list.map(item => (item.noPredPassed ? { ...item, selected: false } : item)),
        },
      }));
      setNotIncludeNoPredPassed(true);
      markDoNotIncludeUsed();
    }
  };

  return (
    <div className="w-full h-full" style={{ minHeight: '45vh' }}>
      <Tooltip target=".multiple-classes-chip" position="top" style={{ maxWidth: '22rem' }} />
      <TabView
        className="w-full h-full nc_diagnostic-modal-tab-view nc_assignment-tabs"
        activeIndex={activeIndex}
        onTabChange={e => setActiveIndex(e.index)}
      >
        {Object.keys(studentsLists).map((key, index) => (
          <TabPanel
            key={key}
            className={`nc_diagnostic-modal-tab-panel ${activeIndex === index ? 'active' : ''}`}
            header={
              <>
                {studentsLists[key].header}{' '}
                <span className="tw-rounded-lg tw-text-xs tw-font-bold tw-px-[6px] tw-py-[4px]">
                  {studentsLists[key].list.length}
                </span>
              </>
            }
          >
            {key === 'buildingFoundations' && (
              <div className="flex align-items-center justify-content-between mb-3">
                <span className="font-semibold">
                  <i className="nc_icon-nc_warning mr-1" style={{ color: '#FDB814' }} />
                  Students who have not completed any of the support skills for this skill.
                </span>
                <Button
                  link
                  className="font-semibold p-0 nc_assignment-plain-link"
                  label="Do not include"
                  id="not-include-pred-checkbox"
                  onClick={dontIncludeNoPredPassed}
                />
              </div>
            )}
            {orderedClasses.map(selectedClass => {
              const students = studentsFor(key, selectedClass.id);
              const allChecked = students.length > 0 && students.every(student => student.selected);

              return (
                <div
                  key={selectedClass.id}
                  className="mb-3 nc_assignment-class-card"
                  role="group"
                  aria-labelledby={`${key}-${selectedClass.id}-label`}
                >
                  <div className="flex align-items-center justify-content-between nc_assignment-class-card__header">
                    <div className="flex align-items-center">
                      <Checkbox
                        onChange={() => selectAllInClass(key, selectedClass.id, !allChecked)}
                        checked={allChecked}
                        disabled={!students.length}
                        inputId={`${key}-${selectedClass.id}`}
                      />
                      <label
                        id={`${key}-${selectedClass.id}-label`}
                        className="ml-2 font-semibold"
                        htmlFor={`${key}-${selectedClass.id}`}
                      >
                        {selectedClass.sectionName}
                      </label>
                    </div>
                    <span className="text-color-secondary">
                      {students.length} {students.length === 1 ? 'student' : 'students'}
                    </span>
                  </div>
                  {!students.length ? (
                    <p className="tw-font-bold text-center py-3 m-0">No students</p>
                  ) : (
                    <div className="grid p-2 m-0">
                      {students.map(student => (
                        <div key={studentRowKey(student)} className="col-6 flex align-items-center py-2">
                          <Checkbox
                            onChange={() => handleCheck(student, key)}
                            checked={student.selected}
                            inputId={studentRowKey(student)}
                          />
                          <label className="ml-1" htmlFor={studentRowKey(student)}>
                            <MaskedStudentData type={PiiTypes.FullName} student={student}>
                              {`${student.studentFirstName} ${student.studentLastName}`}
                            </MaskedStudentData>
                          </label>
                          {student.selectedClassCount > 1 && (
                            <span
                              className="multiple-classes-chip nc_assignment-class-chip ml-2"
                              data-pr-tooltip={multipleClassesMessage}
                            >
                              +{student.selectedClassCount - 1} class
                            </span>
                          )}
                          {student.noPredPassed && (
                            <i className="nc_icon-nc_warning ml-1" style={{ color: '#FDB814' }} />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </TabPanel>
        ))}
      </TabView>
    </div>
  );
};
